/**
 * pages/high_risk/workApproval/index.js — АЖИЛД ГАРГАХ ЗӨВШӨӨРӨЛ
 *
 * Зам: /highrisk/workapproval
 *
 * Урсгал (5А / 15 асуумжийн урьдчилсан шалгалт):
 *   1. Ажилтан шалгалтаа бөглөж ИЛГЭЭНЭ — «Үгүй» хариулт байсан ч
 *      түгжигдэхгүй. Бүртгэл нь «Шийдвэр хүлээж буй» болно.
 *   2. АНХАН ШАТНЫ УДИРДАХ АЖИЛТАН (наряд өгөгч мастер/механик) энэ хуудсанд
 *      хариултуудыг хараад тухайн ажилтныг АЖИЛД ГАРГАХ эсэхийг шийднэ.
 *
 * Хуудасны бүтэц: ХЯНАЛТ (KPI) → ШҮҮЛТ → ЖАГСААЛТ → хариултын дэлгэрэнгүй.
 *
 * Дата: /api/mobile/hazard/registertn (DB) — localStorage биш.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  ConfigProvider,
  DatePicker,
  Empty,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { SearchInput } from "src/components/filters";
import PageHelp from "src/components/help/PageHelp";
import {
  decideOeaWorkApproval,
  listOeaRegistrations,
} from "src/api/hazard";

const { RangePicker } = DatePicker;
const { TextArea } = Input;

const GREEN = "#16A34A";
const ORANGE = "#EA580C";
const RED = "#DC2626";
const BRAND = "#0F6E56";

const STATUS_META = {
  pending: { label: "Шийдвэр хүлээж буй", color: "gold" },
  approved: { label: "Ажилд гаргасан", color: "green" },
  rejected: { label: "Гаргаагүй", color: "red" },
};

const ANSWER_LABEL = { YES: "Тийм", NO: "Үгүй", NA: "Хамаарахгүй" };

function KpiCard({ label, value, sub, icon, color }) {
  return (
    <Card bodyStyle={{ padding: 14 }} style={{ borderRadius: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.4,
            color: "#8A94A6",
            textTransform: "uppercase",
          }}
        >
          {label}
        </span>
        <i className={"fas " + icon} style={{ color, fontSize: 16 }} />
      </div>
      <p style={{ fontSize: 26, fontWeight: 800, margin: "6px 0 0", color: "#1F2430" }}>
        {value}
      </p>
      {sub ? <span style={{ fontSize: 12, color: "#8A94A6" }}>{sub}</span> : null}
    </Card>
  );
}

export default function WorkApproval() {
  const navigate = useNavigate();
  // Анхдагч хугацаа — тухайн сар
  const [range, setRange] = useState([
    moment().startOf("month"),
    moment().endOf("month"),
  ]);
  const [status, setStatus] = useState("pending");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(
        await listOeaRegistrations({
          from_date: range?.[0]?.format("YYYY-MM-DD"),
          to_date: range?.[1]?.format("YYYY-MM-DD"),
        }),
      );
    } catch (e) {
      message.error(
        e?.response?.data?.message || "Мэдээллийг татаж чадсангүй.",
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  // Шүүлт — клиент талд (өдрийн бүртгэл цөөн тул хангалттай)
  const filtered = useMemo(() => {
    const kw = q.trim().toLowerCase();
    return rows.filter((r) => {
      const submitted = r.checklist_5a_completed || r.checklist_15_completed;
      const st = r.work_approval_status || (submitted ? "pending" : null);
      if (status === "pending" && st !== "pending") return false;
      if (status === "approved" && st !== "approved") return false;
      if (status === "rejected" && st !== "rejected") return false;
      if (status === "nosubmit" && submitted) return false;
      if (kw) {
        const hay = `${r.tn} ${r.worker_name} ${r.position} ${r.destination} ${r.work_description} ${r.department_name} ${r.section_name}`.toLowerCase();
        if (!hay.includes(kw)) return false;
      }
      return true;
    });
  }, [rows, status, q]);

  const summary = useMemo(() => {
    const withSubmit = rows.filter(
      (r) => r.checklist_5a_completed || r.checklist_15_completed,
    );
    return {
      total: rows.length,
      submitted: withSubmit.length,
      pending: withSubmit.filter(
        (r) => (r.work_approval_status || "pending") === "pending",
      ).length,
      approved: rows.filter((r) => r.work_approval_status === "approved").length,
      rejected: rows.filter((r) => r.work_approval_status === "rejected").length,
      withNo: withSubmit.filter((r) => (r.checklist_no_count || 0) > 0).length,
    };
  }, [rows]);

  const decide = async (row, decision) => {
    let note = "";
    if (decision === "rejected") {
      const { value, isConfirmed } = await Swal.fire({
        icon: "warning",
        title: "Ажилд гаргахгүй",
        input: "textarea",
        inputLabel: "Шалтгаан (заавал)",
        inputPlaceholder: "Юуг засах, хангах шаардлагатайг бичнэ үү…",
        showCancelButton: true,
        confirmButtonText: "Гаргахгүй",
        cancelButtonText: "Болих",
        inputValidator: (v) => (!v || !v.trim() ? "Шалтгаанаа бичнэ үү" : null),
      });
      if (!isConfirmed) return;
      note = value;
    } else {
      const { isConfirmed, value } = await Swal.fire({
        icon: "question",
        title: "Ажилд гаргах уу?",
        text: `${row.worker_name} — ${row.destination || ""}`,
        input: "textarea",
        inputLabel: "Тэмдэглэл (заавал биш)",
        showCancelButton: true,
        confirmButtonText: "Гаргах",
        cancelButtonText: "Болих",
      });
      if (!isConfirmed) return;
      note = value || "";
    }
    try {
      await decideOeaWorkApproval(row.id, { status: decision, note });
      message.success(
        decision === "approved"
          ? "Ажилд гаргалаа."
          : "Гаргахгүй гэж тэмдэглэлээ.",
      );
      load();
    } catch (e) {
      message.error(e?.response?.data?.message || "Шийдвэр хадгалж чадсангүй.");
    }
  };

  return (
    <div style={{ padding: 16 }}>
      {/* Толгой */}
      <div
        style={{
          marginBottom: 14,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <div>
          <h4 style={{ fontWeight: 800, color: BRAND, marginBottom: 2 }}>
            <i className="fas fa-user-check" style={{ marginRight: 8 }} />
            Ажилд гаргах зөвшөөрөл
            <PageHelp style={{ marginLeft: 2 }} />
          </h4>
          <span style={{ color: "#8A94A6", fontSize: 12 }}>
            Урьдчилсан шалгалт (5А / 15 асуумж) бөглөсөн ажилтныг ажилд гаргах
            эсэхийг анхан шатны удирдах ажилтан шийднэ
          </span>
        </div>
        <Button onClick={() => navigate("/naryad/register")}>
          <i className="fas fa-arrow-left" style={{ marginRight: 6 }} />
          Нарядын жагсаалт руу буцах
        </Button>
      </div>

      {/* ХЯНАЛТЫН ХЭСЭГ */}
      <Row gutter={[12, 12]} style={{ marginBottom: 10 }}>
        <Col xs={12} md={6}>
          <KpiCard
            label="Шийдвэр хүлээж буй"
            value={summary.pending}
            sub={`${summary.submitted} шалгалт илгээсэн`}
            icon="fa-hourglass-half"
            color={ORANGE}
          />
        </Col>
        <Col xs={12} md={6}>
          <KpiCard
            label="Ажилд гаргасан"
            value={summary.approved}
            sub="зөвшөөрөл олгосон"
            icon="fa-user-check"
            color={GREEN}
          />
        </Col>
        <Col xs={12} md={6}>
          <KpiCard
            label="Гаргаагүй"
            value={summary.rejected}
            sub="буцаасан"
            icon="fa-user-slash"
            color={RED}
          />
        </Col>
        <Col xs={12} md={6}>
          <KpiCard
            label="«Үгүй» хариулттай"
            value={summary.withNo}
            sub="анхаарал шаардсан"
            icon="fa-exclamation-triangle"
            color={ORANGE}
          />
        </Col>
      </Row>

      {/* ШҮҮЛТИЙН ХЭСЭГ */}
      <Card bodyStyle={{ padding: 8 }} style={{ marginBottom: 10 }}>
        <ConfigProvider componentSize="small">
          <Space wrap size={6}>
            <span style={{ fontWeight: 700, fontSize: 12 }}>Эхлэл — Төгсгөл:</span>
            <RangePicker
              value={range}
              onChange={setRange}
              format="YYYY-MM-DD"
              allowClear={false}
              ranges={{
                Өнөөдөр: [moment(), moment()],
                "Энэ 7 хоног": [
                  moment().startOf("isoWeek"),
                  moment().endOf("isoWeek"),
                ],
                "Энэ сар": [moment().startOf("month"), moment().endOf("month")],
              }}
            />
            <Select
              style={{ width: 190 }}
              value={status}
              onChange={setStatus}
              options={[
                { value: "pending", label: "Шийдвэр хүлээж буй" },
                { value: "approved", label: "Ажилд гаргасан" },
                { value: "rejected", label: "Гаргаагүй" },
                { value: "nosubmit", label: "Шалгалт бөглөөгүй" },
                { value: "", label: "Бүгд" },
              ]}
            />
            {/* Шүүлт нь client талд (useMemo, мөр 122-136) — амьд хайлт */}
            <SearchInput
              value={q}
              onChange={setQ}
              placeholder="ТН, нэр, хаашаа…"
              style={{ width: 230 }}
            />
            <Button onClick={load} loading={loading}>
              <i className="fas fa-sync" style={{ marginRight: 6 }} />
              Шинэчлэх
            </Button>
          </Space>
        </ConfigProvider>
      </Card>

      {/* ЖАГСААЛТ */}
      <Card bodyStyle={{ padding: 0 }}>
        {loading && !rows.length ? (
          <div style={{ textAlign: "center", padding: 50 }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table
            rowKey="id"
            size="small"
            dataSource={filtered}
            pagination={{ pageSize: 20, showSizeChanger: false }}
            locale={{
              emptyText: (
                <Empty description="Бүртгэл алга" style={{ padding: 30 }} />
              ),
            }}
            columns={[
              { title: "Огноо", dataIndex: "work_date", width: 105 },
              {
                title: "Ажилтан",
                render: (_, r) => (
                  <Space direction="vertical" size={0}>
                    <b>{r.worker_name}</b>
                    <span style={{ fontSize: 11, color: "#8A94A6" }}>
                      {r.tn} · {r.position || "-"}
                    </span>
                  </Space>
                ),
              },
              {
                title: "Нэгж",
                width: 180,
                render: (_, r) => (
                  <Space direction="vertical" size={0}>
                    <span>{r.department_name || "-"}</span>
                    {r.section_name ? (
                      <span style={{ fontSize: 11, color: "#8A94A6" }}>
                        {r.section_name}
                      </span>
                    ) : null}
                  </Space>
                ),
              },
              {
                title: "Ажил",
                onCell: () => ({
                  style: { whiteSpace: "normal", wordBreak: "break-word" },
                }),
                render: (_, r) => (
                  <Space direction="vertical" size={0}>
                    <span>{r.destination || "-"}</span>
                    {r.work_description ? (
                      <span style={{ fontSize: 11, color: "#8A94A6" }}>
                        {r.work_description}
                      </span>
                    ) : null}
                  </Space>
                ),
              },
              {
                title: "Шалгалт",
                width: 140,
                align: "center",
                render: (_, r) => {
                  const done =
                    r.checklist_5a_completed || r.checklist_15_completed;
                  if (!done) return <Tag>Бөглөөгүй</Tag>;
                  const type = r.checklist_5a_completed ? "5А" : "15";
                  const no = r.checklist_no_count || 0;
                  return (
                    <Space direction="vertical" size={2}>
                      <Tag color="blue">{type} бөглөсөн</Tag>
                      {no > 0 ? (
                        <Tag color="orange">{no} «Үгүй»</Tag>
                      ) : (
                        <Tag color="green">Бүгд хэвийн</Tag>
                      )}
                    </Space>
                  );
                },
              },
              {
                title: "Төлөв",
                width: 165,
                render: (_, r) => {
                  const done =
                    r.checklist_5a_completed || r.checklist_15_completed;
                  const st = r.work_approval_status || (done ? "pending" : null);
                  if (!st) return <Tag>Шалгалт хүлээгдэж буй</Tag>;
                  const meta = STATUS_META[st] || {};
                  return (
                    <Tooltip title={r.work_approval_note || ""}>
                      <Space direction="vertical" size={0}>
                        <Tag color={meta.color}>{meta.label || st}</Tag>
                        {r.work_approval_by_name ? (
                          <span style={{ fontSize: 11, color: "#8A94A6" }}>
                            {r.work_approval_by_name}
                          </span>
                        ) : null}
                      </Space>
                    </Tooltip>
                  );
                },
              },
              {
                title: "Шийдвэр",
                width: 200,
                render: (_, r) => {
                  const done =
                    r.checklist_5a_completed || r.checklist_15_completed;
                  if (!done) return <span style={{ color: "#B6BCC8" }}>—</span>;
                  return (
                    <Space size={4} wrap>
                      <Button size="small" onClick={() => setDetail(r)}>
                        Хариулт
                      </Button>
                      <Button
                        size="small"
                        type="primary"
                        style={{ background: GREEN, borderColor: GREEN }}
                        onClick={() => decide(r, "approved")}
                      >
                        Гаргах
                      </Button>
                      <Button
                        size="small"
                        danger
                        onClick={() => decide(r, "rejected")}
                      >
                        Гаргахгүй
                      </Button>
                    </Space>
                  );
                },
              },
            ]}
          />
        )}
      </Card>

      {detail ? (
        <AnswersModal row={detail} onClose={() => setDetail(null)} />
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 *  Шалгалтын хариултууд — зөвхөн харах
 * ------------------------------------------------------------------ */
function AnswersModal({ row, onClose }) {
  const answers =
    row.checklist_5a_answers || row.checklist_15_answers || [];
  const list = Array.isArray(answers) ? answers : [];
  return (
    <Modal
      open
      width={700}
      title={
        <Space>
          <b>{row.worker_name}</b>
          <span style={{ color: "#8A94A6", fontSize: 12 }}>
            {row.work_date} · {row.checklist_5a_completed ? "5А" : "15 асуумж"}
          </span>
        </Space>
      }
      onCancel={onClose}
      footer={<Button onClick={onClose}>Хаах</Button>}
    >
      {!list.length ? (
        <Empty description="Хариултын дэлгэрэнгүй хадгалагдаагүй байна." />
      ) : (
        <Table
          rowKey={(r, i) => String(r.id ?? i)}
          size="small"
          pagination={false}
          dataSource={list}
          columns={[
            { title: "№", dataIndex: "no", width: 55, align: "center" },
            { title: "Асуумж", dataIndex: "title" },
            {
              title: "Хариулт",
              dataIndex: "answer",
              width: 130,
              align: "center",
              render: (v) => (
                <Tag
                  color={v === "NO" ? "red" : v === "YES" ? "green" : "default"}
                >
                  {ANSWER_LABEL[v] || "—"}
                </Tag>
              ),
            },
          ]}
        />
      )}
      {row.work_approval_note ? (
        <Card size="small" style={{ marginTop: 12 }} title="Удирдлагын тэмдэглэл">
          {row.work_approval_note}
        </Card>
      ) : null}
    </Modal>
  );
}
